import Paystack from "@paystack/inline-js";
import classNames from "classnames";
import { FormikErrors, FormikTouched, useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { BackButton } from "../../../components/buttons/backButton";
import { Button } from "../../../components/buttons/button";
import { ProfileIcon } from "../../../components/cards/profilePic";
import { Icon } from "../../../components/icon/icon";
import { Checkbox } from "../../../components/inputs/checkbox";
import { SwitchInput } from "../../../components/inputs/switch";
import { TextInput } from "../../../components/inputs/textInput";
import { ScrollToTop } from "../../../components/scrollToTop";
import { MiniHeader } from "../../../components/typography/miniHeader";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import {
  useBuyTicktesMutation,
  useValidateDiscountMutation,
} from "../../../redux/features/event/event.api";
import {
  BuyTicketsPayload,
  CreatedEvent,
  CreatedTicket,
} from "../../../redux/features/event/event.types";
import { PaystackSuccessData } from "../../../redux/features/payment/payment.types";
import { useAppSelector } from "../../../redux/hooks";
import {
  calcFees,
  calcTotalTicketPrice,
  formatToNaira,
} from "../../../utils/helpers";
import { BaseApiErrorResponse, TicketPurchase } from "../../../utils/types";
import { PurchaseTicketSchema } from "../../../utils/validations";
import { ChooseTickets } from "./chooseTickets";
import { PurchaseSuccess } from "./purchaseSuccess";

const payStackPopup = new Paystack();

type SingleGuest = {
  firstName: string;
  lastName: string;
  email: string;
  title: string;
  ticketId: string;
};

export const PurchaseTickets = ({
  event,
  setStep,
  tickets,
  ticketsPurchase,
  setTicketsPurchase,
  individualTicketsPurchase,
}: {
  event: CreatedEvent;
  tickets: CreatedTicket[];
  ticketsPurchase: TicketPurchase[];
  setTicketsPurchase: (data: TicketPurchase[]) => void;
  setStep: (data: 1 | 2) => void;
  individualTicketsPurchase: CreatedTicket[];
}) => {
  const [editingTickets, setEditingTickets] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [total, setTotal] = useState<number>(0);
  const [purchaserIndex, setPurchaserIndex] = useState<number | undefined>();
  const [totalTicketQuantity, setTotalTicketQuantity] = useState(0);

  const { profile, isSignedIn } = useAppSelector((state) => state.auth);

  const containerRef = useRef<HTMLDivElement>(null);

  const [buyTickets, { isLoading: buyingTickets, data: buyTicketResp }] =
    useBuyTicktesMutation();

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleSubmit,
    setFieldValue,
    handleChange,
    validateForm,
  } = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      hasGuests: false,
      guests: [],
      hasDiscountCode: false,
      discountCode: "",
    } as {
      firstName: string;
      lastName: string;
      email: string;
      hasGuests: boolean;
      guests: SingleGuest[];
      hasDiscountCode: boolean;
      discountCode: string;
    },
    enableReinitialize: false,
    validationSchema: PurchaseTicketSchema,
    onSubmit: async (vals) => {
      if (purchaserIndex === undefined && values.hasGuests) {
        toast.error("Assign a ticket to yourself");
        return;
      }

      const payload: BuyTicketsPayload = {
        mainTicketId:
          values.hasGuests && purchaserIndex !== undefined
            ? values.guests[purchaserIndex].ticketId
            : undefined,
        amount: calcTotalTicketPrice(
          total,
          discount?.data?.value,
          discount?.data?.type,
        ),
        currency: "NGN",
        email: vals.email,
        callbackUrl: `${window.location.origin}/payment/success`,
        firstName: vals.firstName,
        lastName: vals.lastName,
        userId: isSignedIn ? profile?._id : undefined,
        tickets: ticketsPurchase.map((tp) => {
          return {
            ticketId: tp.ticket._id,
            quantity: tp.quantity,
          };
        }),
        invitedGuests: vals.hasGuests
          ? vals.guests
              .filter((g, idx) => idx !== purchaserIndex)
              .map((g) => ({
                firstName: g.firstName,
                lastName: g.lastName,
                email: g.email,
                ticketId: g.ticketId,
              }))
          : undefined,
        discountCode:
          vals.hasDiscountCode && !!vals.discountCode && !discountError
            ? vals.discountCode
            : undefined,
        discountCreatedBy:
          vals.hasDiscountCode && !!vals.discountCode && !discountError
            ? (discount?.data.createdBy as unknown as string)
            : undefined,
      };

      const resp = await buyTickets({
        id: event._id,
        data: payload,
      });

      if (resp.data?.status === "success" && resp.data.data.paymentUrl) {
        // @ts-ignore
        payStackPopup.resumeTransaction(
          resp.data.data.payment?.access_code!,
          // @ts-ignore
          {
            onSuccess: (successData: PaystackSuccessData) => {
              setPurchaseSuccess(true);
            },
            onCancel: () => {
              console.log("Payment canceled!");
            },
          },
        );
      } else if (
        resp.data?.status === "success" &&
        !resp.data.data.paymentUrl
      ) {
        setPurchaseSuccess(true);
      }
    },
  });

  const [
    validateDiscount,
    {
      data: discount,
      isLoading: fetchingDiscount,
      error: discountError,
      isError: isDiscountError,
      reset: resetDiscount,
    },
  ] = useValidateDiscountMutation();

  useEffect(() => {
    setFieldValue(
      "guests",
      individualTicketsPurchase.map((tp, idx) => ({
        firstName: "",
        lastName: "",
        email: "",
        title: `Guest ${idx + 1} - ${tp.name}`,
        ticketId: tp._id,
      })),
    );
    setPurchaserIndex(undefined);
    // eslint-disable-next-line
  }, [individualTicketsPurchase]);

  useEffect(() => {
    if (isSignedIn) {
      setFieldValue("firstName", profile?.firstName);
      setFieldValue("lastName", profile?.lastName);
      setFieldValue("email", profile?.email);

      validateForm();
    }
    // eslint-disable-next-line
  }, [profile, isSignedIn]);

  useEffect(() => {
    if (ticketsPurchase.length > 0) {
      const t = ticketsPurchase
        .map((tp) => tp.quantity)
        .reduce((prev, curr, currIdx, arr) => prev + curr);

      setTotalTicketQuantity(t);
    }
    // eslint-disable-next-line
  }, [ticketsPurchase]);

  useEffect(() => {
    const t = ticketsPurchase
      .map((tp) =>
        tp.ticket.ticketType === "free" ? 0 : tp.quantity * tp.ticket.price,
      )
      .reduce((prev, curr, currIdx, arr) => prev + curr);

    setTotal(t);
    // eslint-disable-next-line
  }, [ticketsPurchase]);

  return purchaseSuccess ? (
    <PurchaseSuccess
      purchaseResponse={buyTicketResp!}
      ticketsPurchase={ticketsPurchase}
    />
  ) : (
    <div
      ref={containerRef}
      className="mx-auto flex w-full flex-col gap-3 xl:max-w-full"
    >
      <ScrollToTop ref={containerRef} />
      <BackButton
        onClick={() => {
          setStep(1);
        }}
        text="Back"
      />

      <div className="flex w-full flex-col gap-3 md:flex-row md:flex-wrap md:items-start md:justify-between md:gap-10">
        <div className="relative flex w-full flex-col gap-3 xl:max-w-[600px]">
          <BgWrapper className="flex flex-col gap-4 !p-4">
            <div className="flex items-center justify-between gap-4">
              <MiniHeader header="Tickets" className="!text-grey-700" />

              <button
                type="button"
                className="text-sm font-bold md:text-base"
                onClick={() => {
                  setEditingTickets(true);
                }}
              >
                Edit
              </button>
            </div>

            <ChooseTickets
              tickets={tickets ?? []}
              ticketsPurchase={ticketsPurchase}
              setTicketsPurchase={setTicketsPurchase}
              editableView
              isEdit={editingTickets}
              minPurchaseQuantity={1}
              showControls={editingTickets}
            />
          </BgWrapper>

          <BgWrapper className="flex flex-col gap-4 !p-4">
            <MiniHeader
              header="Contact Information"
              className="!text-grey-700"
            />

            {isSignedIn ? (
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <p className="text-base font-bold text-primary">
                    {values.firstName} {values.lastName}
                  </p>
                  <p className="text-sm text-grey-900">{values.email}</p>
                </div>

                <ProfileIcon
                  dimension={40}
                  img={undefined}
                  className="!p-[10px]"
                />
              </div>
            ) : (
              <div className="grid w-full grid-cols-2 gap-4">
                <TextInput
                  type="text"
                  name="firstName"
                  value={values.firstName}
                  placeholder="Enter first name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  hasTitle
                  title="First name"
                  errors={
                    errors.firstName && touched.firstName
                      ? errors.firstName
                      : ""
                  }
                  readOnly={isSignedIn}
                  autoFocus
                  autoFocusOnMobile={false}
                />

                <TextInput
                  type="text"
                  name="lastName"
                  value={values.lastName}
                  placeholder="Enter last name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  hasTitle
                  title="Last name"
                  errors={
                    errors.lastName && touched.lastName ? errors.lastName : ""
                  }
                  readOnly={isSignedIn}
                />

                <TextInput
                  type="text"
                  name="email"
                  value={values.email}
                  placeholder="Enter email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  hasTitle
                  title="Email"
                  containerClassName="col-span-full"
                  errors={errors.email && touched.email ? errors.email : ""}
                  readOnly={isSignedIn}
                />
              </div>
            )}

            <div
              className={classNames(
                "flex items-center justify-between border-t border-grey-100 pt-4",
                {
                  hidden: values.guests.length === 0,
                },
              )}
            >
              <p className="text-sm font-bold">Send ticket to another email</p>

              <SwitchInput
                checked={values.hasGuests}
                onChange={() => {
                  setFieldValue("hasGuests", !values.hasGuests);
                }}
                disabled={false}
                className=""
              />
            </div>
          </BgWrapper>

          {values.hasGuests && (
            <BgWrapper className="flex flex-col gap-4 !p-4">
              <MiniHeader header="Assign Tickets" className="!text-grey-700" />

              {values.guests.map((guest, idx) => {
                return (
                  <div
                    className="grid w-full grid-cols-2 gap-4 border-b border-grey-100 pb-4 last:border-b-0 last:pb-0"
                    key={`guest-${idx}`}
                  >
                    <p className="col-span-full text-sm font-bold">
                      {guest.title}
                    </p>

                    <TextInput
                      type="text"
                      name={`guests[${idx}].firstName`}
                      value={values.guests[idx].firstName}
                      placeholder="Enter guest's first name"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      hasTitle
                      title="First name"
                      errors={
                        (errors.guests as FormikErrors<SingleGuest>[])?.length >
                          0 &&
                        (touched.guests as FormikTouched<SingleGuest>[])
                          ?.length > 0 &&
                        (errors.guests as FormikErrors<SingleGuest>[])[idx]
                          ?.firstName &&
                        (touched.guests as FormikTouched<SingleGuest>[])[idx]
                          ?.firstName &&
                        purchaserIndex !== idx
                          ? (errors.guests as FormikErrors<SingleGuest>[])[idx]
                              .firstName
                          : ""
                      }
                      readOnly={purchaserIndex === idx}
                    />

                    <TextInput
                      type="text"
                      name={`guests[${idx}].lastName`}
                      value={values.guests[idx].lastName}
                      placeholder="Enter guest's last name"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      hasTitle
                      title="Last name"
                      errors={
                        (errors.guests as FormikErrors<SingleGuest>[])?.length >
                          0 &&
                        (touched.guests as FormikTouched<SingleGuest>[])
                          ?.length > 0 &&
                        (errors.guests as FormikErrors<SingleGuest>[])[idx]
                          ?.lastName &&
                        (touched.guests as FormikTouched<SingleGuest>[])[idx]
                          ?.lastName &&
                        purchaserIndex !== idx
                          ? (errors.guests as FormikErrors<SingleGuest>[])[idx]
                              .lastName
                          : ""
                      }
                      readOnly={purchaserIndex === idx}
                    />

                    <TextInput
                      type="text"
                      name={`guests[${idx}].email`}
                      value={values.guests[idx].email}
                      placeholder="Enter guest's email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      hasTitle
                      title="Email"
                      containerClassName="col-span-full"
                      errors={
                        (errors.guests as FormikErrors<SingleGuest>[])?.length >
                          0 &&
                        (touched.guests as FormikTouched<SingleGuest>[])
                          ?.length > 0 &&
                        (errors.guests as FormikErrors<SingleGuest>[])[idx]
                          ?.email &&
                        (touched.guests as FormikTouched<SingleGuest>[])[idx]
                          ?.email &&
                        purchaserIndex !== idx
                          ? (errors.guests as FormikErrors<SingleGuest>[])[idx]
                              .email
                          : ""
                      }
                      readOnly={purchaserIndex === idx}
                    />

                    <div className="col-span-full">
                      <Checkbox
                        disabled={
                          !values.firstName || !values.lastName || !values.email
                        }
                        checkStatus={purchaserIndex === idx}
                        handleChange={async (checked) => {
                          if (checked) {
                            if (purchaserIndex !== undefined) {
                              setFieldValue(
                                `guests[${purchaserIndex}].firstName`,
                                "",
                              );
                              setFieldValue(
                                `guests[${purchaserIndex}].lastName`,
                                "",
                              );
                              setFieldValue(
                                `guests[${purchaserIndex}].email`,
                                "",
                              );
                            }

                            setPurchaserIndex(idx);

                            setFieldValue(
                              `guests[${idx}].firstName`,
                              values.firstName,
                            );
                            setFieldValue(
                              `guests[${idx}].lastName`,
                              values.lastName,
                            );
                            setFieldValue(`guests[${idx}].email`, values.email);
                          } else {
                            setFieldValue(
                              `guests[${purchaserIndex}].firstName`,
                              "",
                            );
                            setFieldValue(
                              `guests[${purchaserIndex}].lastName`,
                              "",
                            );
                            setFieldValue(
                              `guests[${purchaserIndex}].email`,
                              "",
                            );

                            setPurchaserIndex(undefined);
                          }
                        }}
                        text="This is my ticket"
                      />
                    </div>
                  </div>
                );
              })}
            </BgWrapper>
          )}
        </div>

        <div className="sticky top-0 w-full xl:max-w-[400px]">
          <BgWrapper className="flex w-full flex-col gap-4 !p-4">
            <MiniHeader header="Summary" />

            <div className="flex flex-col gap-4 border-b border-grey-100 pb-4">
              {ticketsPurchase.map((tp, idx) => {
                return (
                  <div
                    key={`${tp.ticket._id}-${idx}`}
                    className="flex items-center justify-between"
                  >
                    <p className="text-grey-500">
                      {tp.quantity}x {tp.ticket.name}
                    </p>
                    <p className="font-bold">
                      {tp.ticket.ticketType === "paid"
                        ? formatToNaira(tp.quantity * tp.ticket.price)
                        : formatToNaira(0)}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col gap-4 border-b border-grey-100 pb-4">
              {discount?.data && (
                <div className="flex items-center justify-between">
                  <p className="text-grey-500">Discount</p>
                  <p className="font-bold">
                    -
                    {formatToNaira(
                      discount.data.type === "amount"
                        ? discount.data.value
                        : (discount.data.value / 100) * total,
                    )}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between">
                <p className="text-grey-500">Fees</p>
                <p className="font-bold">
                  {formatToNaira(
                    calcFees(
                      total,
                      discount?.data?.value,
                      discount?.data?.type,
                    ),
                  )}
                </p>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-grey-500">Subtotal</p>
                <p className="font-bold">
                  {formatToNaira(
                    calcTotalTicketPrice(
                      total,
                      discount?.data?.value,
                      discount?.data?.type,
                    ),
                  )}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 border-b border-grey-100 pb-4">
              {!values.hasDiscountCode ? (
                <button
                  className="flex w-fit items-center gap-1 text-base font-bold"
                  type="button"
                  onClick={() => {
                    setFieldValue("hasDiscountCode", true);
                  }}
                >
                  <Icon svg="plus" />
                  Add discount code
                </button>
              ) : (
                <>
                  {discount?.status === "success" && values.discountCode ? (
                    <div className="flex w-full items-center gap-1 text-success">
                      <Icon svg="check" width={20} height={20} />
                      <MiniHeader
                        header="Discount applied"
                        className="!text-success"
                      />
                    </div>
                  ) : (
                    <MiniHeader header="Add discount code" className="w-full" />
                  )}

                  {discount?.status === "success" && values.discountCode ? (
                    <div className="flex w-full items-start gap-3 rounded-md border border-grey-100 p-3">
                      <div className="flex flex-1 flex-col gap-[2px] text-sm">
                        <p className="font-bold text-grey-900">
                          {discount.data.name}
                        </p>
                        <p className="text-grey-500">
                          {discount.data.type === "amount"
                            ? formatToNaira(discount.data.value)
                            : `${discount.data.value}%`}{" "}
                          OFF
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          resetDiscount();
                          setFieldValue("hasDiscountCode", false);
                          setFieldValue("discountCode", "");
                        }}
                        className="w-fit text-base font-bold text-danger"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <>
                      <TextInput
                        type="text"
                        name="discountCode"
                        value={values.discountCode}
                        placeholder="Enter discount code"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        hasTitle
                        title="Discount Code"
                        errors={
                          isDiscountError
                            ? (discountError as BaseApiErrorResponse)?.data
                                ?.message
                            : ""
                        }
                        containerClassName="flex-1"
                      />

                      <Button
                        type="button"
                        className="!h-fit !max-w-[100px]"
                        variation="plain-grey"
                        text="Apply"
                        disabled={["", undefined, 0].includes(
                          values.discountCode.length,
                        )}
                        loading={fetchingDiscount}
                        onClick={async () => {
                          await validateDiscount({
                            code: values.discountCode.replaceAll(" ", ""),
                            userId: event.createdBy._id,
                            eventId: event._id,
                          });
                        }}
                      />
                    </>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center justify-between">
              <p className="text-grey-500">Total</p>
              <p className="font-bold">
                {formatToNaira(
                  calcTotalTicketPrice(
                    total,
                    discount?.data?.value,
                    discount?.data?.type,
                  ),
                )}
              </p>
            </div>

            <Button
              type="button"
              onClick={() => {
                handleSubmit();
              }}
              text={
                calcTotalTicketPrice(
                  total,
                  discount?.data?.value,
                  discount?.data?.type,
                ) === 0
                  ? totalTicketQuantity > 1
                    ? "Get Tickets"
                    : "Get Ticket"
                  : totalTicketQuantity > 1
                    ? "Buy Tickets"
                    : "Buy Ticket"
              }
              disabled={buyingTickets}
              loading={buyingTickets}
            />

            <div className="flex w-full items-center justify-center">
              {/* <Icon svg="secured-paystack" width={150} height={32} /> */}
              <img
                src="/assets/images/paystackSecured.png"
                alt="paystack"
                className="block w-full max-w-[150px] object-contain"
              />
            </div>

            <p className="text-center text-sm text-grey-500">
              By clicking “Buy Ticket” you agree to our
              <br />
              <Link to={"/legal/terms-of-use"} className="underline">
                Term of Use
              </Link>
              ,{" "}
              <Link to={"/legal/refund-policy"} className="underline">
                Refund Policy
              </Link>{" "}
              and{" "}
              <Link to={"/legal/privacy-policy"} className="underline">
                Privacy Policy
              </Link>
            </p>
          </BgWrapper>
        </div>
      </div>
    </div>
  );
};
